/**
 * Reviewer-facing reason for a failed review-projection request (#6826).
 *
 * ## The defect this closes
 *
 * `useReviewProjection` set its error state from `err.message`, and every
 * failure rendered as the HTTP status text:
 *
 *     Couldn't load the approval forecast: Conflict.
 *
 * The server was answering precisely — 409 `DOC-000002` with a message naming
 * the remedy — and none of it reached the reviewer. "Conflict" is not a
 * reason, and it is indistinguishable from a 409 raised by anything else.
 *
 * Browser-verified against the live local stack: the network tab carried the
 * full envelope while the alert showed the status text alone.
 *
 * ## Where the code actually lives, which is not where you would look
 *
 * `httpClient` runs each rejection through `@aegle/ra-commons`, which
 * normalizes `body` to an `AegleApiError` — `{status, message, category,
 * correlationId, raw}`. That type has NO code field. The dataProvider's outer
 * catch re-attaches one, but reads `raw.body.code` (lowercase) while mgmt-api
 * emits `Code`, so it never populates for our API.
 *
 * Net effect, confirmed by running the real normalizer:
 *
 *     error.body.raw.body.Code      <- the ONLY place the code survives
 *     error.body.code               <- never set for mgmt-api (casing bug)
 *     error.body.message            <- the server's Message, via
 *                                      promoteApiErrorBodyToMessage
 *
 * This module's first version read `error.body.Code`. Wrong, and it FAILED
 * SILENTLY into the message fallback — so the alert showed the server's raw
 * developer-facing prose instead of "Conflict" and looked like progress. It
 * was caught in the browser, not by its seven unit tests, because those tests
 * asserted against a fixture shape invented rather than observed. The fixtures
 * here are built by the real `createErrorAwareHttpClient`.
 */

import { apiErrorCode, apiErrorMessage } from "@/utils/errors/apiErrorCode";

/** Error codes the projection endpoint answers with. */
export const PROJECTION_ERROR_CODES = {
  /** Record index absent or written under a superseded identity (#6893). */
  RECORD_INDEX_UNAVAILABLE: "DOC-000001",
  /** Whole-document record set exceeds mgmt-api's hydration bound (#6826). */
  RECORD_SET_TOO_LARGE: "DOC-000002",
  /** record_summary cannot prove a window won't split a claimant (#6755). */
  WINDOW_UNSAFE: "DOC-000003",
} as const;

// The lookup lives in one place (src/utils/errors/apiErrorCode.ts), shared with
// the conditional-laser route checks (!4880 thread 30530).
const apiCode = apiErrorCode;
const apiMessage = apiErrorMessage;

/**
 * A short lowercase clause, composed into
 *
 *     Couldn't load the approval forecast: {reason}. The grid still reflects
 *     extracted data.
 *
 * so it must not be a sentence and must not end in a period — the wrapper adds
 * one, and the server's own messages do too, which is where the ".." came from.
 *
 * The mapped copy is deliberately NOT the server's. `DOC-000002`'s remedy
 * reads "request a windowed projection (from_record_index / limit)" — accurate
 * for an API caller, and meaningless to a reviewer, who does not issue
 * requests. The code is the contract
 * (backend_api_error_handling_standard.md: clients must not branch on message
 * text); the sentence a human reads belongs here.
 *
 * Never returns an empty string: a blank reason reads as a rendering bug
 * rather than a server answer, which is worse than the "Conflict" it replaces.
 */
export const projectionErrorReason = (error: unknown): string => {
  switch (apiCode(error)) {
    case PROJECTION_ERROR_CODES.RECORD_SET_TOO_LARGE:
      // Reaching this means the server's bound is below this document's size
      // while the client judged it small enough to ask. The two mirror the
      // same 25,000 and are documented to stay in sync, so a disagreement is a
      // deploy skew or a stale bundle — not a property of the document, and
      // not something the reviewer did. Reload is the action that fixes it.
      return "this document is too large — reload the page, and let support know if it keeps happening";

    case PROJECTION_ERROR_CODES.WINDOW_UNSAFE:
      return "this document needs to be reprocessed first";

    case PROJECTION_ERROR_CODES.RECORD_INDEX_UNAVAILABLE:
      return "this document was processed before forecasts existed — reprocess it to see them";

    default:
      break;
  }

  // Unknown code, or no envelope at all (a network failure has no body). The
  // server's own message first — still strictly better than the status text,
  // and what makes this a fix for every future error rather than a three-code
  // lookup. Trailing punctuation stripped so the wrapper's period is the only
  // one.
  const fallback = apiMessage(error) ?? (error instanceof Error ? error.message.trim() : "");
  const trimmed = fallback.replace(/[.\s]+$/, "");
  return trimmed || "the server did not give a reason";
};
